import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Users, Search } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TenantBulkGrid from "@/components/TenantBulkGrid";

const empty = { full_name: "", room_no: "", whatsapp: "", monthly_rent: "", security_deposit: "", refundable_amount: "", sharing_type: "1", floor: "", email: "", remarks: "", deposit_refundable: true, joining_date: "", active: true };

const toISO = (d) => {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const m = d.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};
const toDMY = (d) => {
  if (!d) return null;
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

export default function TenantsTab({ tenants, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(null);

  const startAdd = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (t) => {
    setEditing(t);
    const merged = { ...empty, ...t };
    for (const k of Object.keys(merged)) if (merged[k] == null) merged[k] = typeof empty[k] === "boolean" ? empty[k] : "";
    setForm({ ...merged, monthly_rent: String(t.monthly_rent ?? ""), security_deposit: String(t.security_deposit ?? ""), refundable_amount: String(t.refundable_amount ?? ""), joining_date: toISO(t.joining_date || "") });
    setOpen(true);
  };
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.full_name || !form.room_no || !form.whatsapp || !form.monthly_rent)
      return toast.error("Name, room, WhatsApp and rent are required");
    setBusy(true);
    const payload = {
      full_name: form.full_name,
      room_no: form.room_no,
      whatsapp: form.whatsapp,
      monthly_rent: parseFloat(form.monthly_rent) || 0,
      security_deposit: parseFloat(form.security_deposit) || 0,
      refundable_amount: parseFloat(form.refundable_amount) || 0,
      sharing_type: form.sharing_type || null,
      floor: form.floor || null,
      email: form.email || null,
      remarks: form.remarks || null,
      deposit_refundable: !!form.deposit_refundable,
      joining_date: toDMY(form.joining_date),
      active: !!form.active,
    };
    try {
      if (editing) await api.put(`/tenants/${editing.id}`, payload);
      else await api.post("/tenants", payload);
      toast.success(editing ? "Tenant updated" : "Tenant added");
      setOpen(false);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete tenant ${t.full_name}?`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Tenant removed"); onChange();
    } catch (e) { toast.error("Delete failed"); }
  };

  const toggleActive = async (t) => {
    const next = !(t.active ?? true);
    setToggling(t.id);
    try {
      await api.put(`/tenants/${t.id}`, {
        full_name: t.full_name,
        room_no: t.room_no,
        whatsapp: t.whatsapp || null,
        monthly_rent: t.monthly_rent ?? 0,
        security_deposit: t.security_deposit ?? 0,
        refundable_amount: t.refundable_amount ?? 0,
        sharing_type: t.sharing_type || null,
        floor: t.floor || null,
        email: t.email || null,
        remarks: t.remarks || null,
        deposit_refundable: t.deposit_refundable ?? true,
        joining_date: t.joining_date || null,
        active: next,
      });
      toast.success(`${t.full_name} marked ${next ? "Active" : "Inactive"}`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
    finally { setToggling(null); }
  };

  const sortedTenants = [...tenants].sort((a, b) => {
    const ra = parseInt(a.room_no, 10);
    const rb = parseInt(b.room_no, 10);
    if (!isNaN(ra) && !isNaN(rb) && ra !== rb) return ra - rb;
    if (!isNaN(ra) && isNaN(rb)) return -1;
    if (isNaN(ra) && !isNaN(rb)) return 1;
    const fa = parseInt(a.floor, 10) || 0;
    const fb = parseInt(b.floor, 10) || 0;
    if (fa !== fb) return fa - fb;
    return String(a.room_no).localeCompare(String(b.room_no));
  });

  const visibleTenants = sortedTenants.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (t.full_name || "").toLowerCase().includes(q) || String(t.room_no || "").toLowerCase().includes(q);
  });

  return (
    <Card className="p-6 border-slate-200">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-indigo-600" /> Add Tenants</h2>
          <p className="text-sm text-slate-500 mt-0.5">Everyone currently staying at your property.</p>
        </div>
        <div className="flex items-start gap-2">
          <div className="flex flex-col items-center">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={startAdd} data-testid="tenant-add-button">
              <Plus className="h-4 w-4 mr-1" /> Add tenant
            </Button>
            <span className="text-[10px] text-slate-400 mt-1" data-testid="tenant-add-hint">For adding a single tenant</span>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <TenantBulkGrid onSaved={onChange} />
      </div>

      {tenants.length > 0 && (
        <div className="mb-3 max-w-sm">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by tenant name or room number"
              className="pl-8"
              data-testid="tenant-search-input"
            />
          </div>
        </div>
      )}

      {tenants.length === 0 ? (
        <div className="py-12 text-center text-slate-500">No tenants yet. Click "Add tenant" to create your first entry.</div>
      ) : (
        <div className="overflow-x-auto max-h-[440px] overflow-y-auto rounded-lg border border-slate-100" data-testid="tenants-table-scroll">
          <table className="w-full text-sm min-w-[1350px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-indigo-700 border-b border-indigo-200">
                <Th>Name</Th><Th>Date of Joining</Th><Th>Room No</Th><Th>Floor No</Th><Th>Sharing</Th><Th>Rent</Th><Th>Deposit (Advance)</Th><Th>Refundable</Th><Th>Mobile</Th><Th>Email</Th><Th>Remarks</Th><Th>Status</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {visibleTenants.map((t) => (
                <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-opacity ${t.active === false ? "opacity-60 bg-slate-50/60" : ""}`} data-testid={`tenant-row-${t.id}`}>
                  <Td className="font-medium">{t.full_name}</Td>
                  <Td className="font-mono text-xs">{t.joining_date || "—"}</Td>
                  <Td className="font-mono text-xs">{t.room_no}</Td>
                  <Td className="font-mono text-xs">{t.floor || "—"}</Td>
                  <Td className="font-mono text-xs">{t.sharing_type || "—"}</Td>
                  <Td>₹ {(t.monthly_rent || 0).toLocaleString("en-IN")}</Td>
                  <Td>₹ {(t.security_deposit || 0).toLocaleString("en-IN")}</Td>
                  <Td>₹ {(t.refundable_amount || 0).toLocaleString("en-IN")}</Td>
                  <Td className="font-mono text-xs">{t.whatsapp}</Td>
                  <Td className="text-xs">{t.email || "—"}</Td>
                  <Td className="text-xs text-slate-500 max-w-[140px] truncate" title={t.remarks || ""}>{t.remarks || "—"}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={t.active ?? true}
                        disabled={toggling === t.id}
                        onCheckedChange={() => toggleActive(t)}
                        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300"
                        data-testid={`tenant-status-toggle-${t.id}`}
                      />
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${t.active ? "text-emerald-600" : "text-slate-400"}`} data-testid={`tenant-status-label-${t.id}`}>
                        {t.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </Td>
                  <Td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(t)} data-testid={`tenant-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(t)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`tenant-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </Td>
                </tr>
              ))}
              {visibleTenants.length === 0 && (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-slate-500" data-testid="tenant-search-no-results">
                    No tenants match "{search}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="tenant-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit tenant" : "Add tenant"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <F label="Full name" span2><Input value={form.full_name} onChange={upd("full_name")} maxLength={30} data-testid="tenant-form-name-input" /></F>
            <F label="Date of Joining"><Input type="date" value={form.joining_date || ""} onChange={upd("joining_date")} data-testid="tenant-form-joining-input" /></F>
            <F label="Room No"><Input value={form.room_no} onChange={upd("room_no")} data-testid="tenant-form-room-input" /></F>
            <F label="Floor Number"><Input value={form.floor} onChange={upd("floor")} placeholder="1" data-testid="tenant-form-floor-input" /></F>
            <F label="Sharing type">
              <Select value={form.sharing_type || "1"} onValueChange={(v) => setForm((f) => ({ ...f, sharing_type: v }))}>
                <SelectTrigger data-testid="tenant-form-sharing-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Rent amount (₹)"><Input type="number" value={form.monthly_rent} onChange={upd("monthly_rent")} data-testid="tenant-form-rent-input" /></F>
            <F label="Deposit / Advance (₹)"><Input type="number" value={form.security_deposit} onChange={upd("security_deposit")} data-testid="tenant-form-deposit-input" /></F>
            <F label="Refundable amount (₹)"><Input type="number" value={form.refundable_amount} onChange={upd("refundable_amount")} data-testid="tenant-form-refundable-amount-input" /></F>
            <F label="Mobile number (WhatsApp)"><Input value={form.whatsapp} onChange={upd("whatsapp")} placeholder="+919812340000" data-testid="tenant-form-whatsapp-input" /></F>
            <F label="Email ID"><Input type="email" value={form.email} onChange={upd("email")} placeholder="tenant@example.com" data-testid="tenant-form-email-input" /></F>
            <F label="Remarks (if any)" span2><Input value={form.remarks} onChange={upd("remarks")} placeholder="e.g. prefers corner bed" data-testid="tenant-form-remarks-input" /></F>
          </div>
          <div className="pt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={busy} data-testid="tenant-form-submit-button">
              {busy ? "Saving…" : (editing ? "Save changes" : "Add tenant")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const Th = ({ children, className = "" }) => <th className={`py-3 pr-4 font-semibold sticky top-0 bg-indigo-50 z-10 first:rounded-tl-lg last:rounded-tr-lg ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-3 pr-4 whitespace-nowrap ${className}`}>{children}</td>;

function F({ label, children, span2 }) {
  return (
    <div className={`space-y-1.5 ${span2 ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
